import React, { useState, useEffect, useRef } from "react";

import { matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { Divider, Skeleton, notification, Flex, Button, Col, Form, Grid, Input, Popconfirm, Row, Select } from "antd";
import { useMutation } from "@tanstack/react-query";

import { useForm } from "antd/lib/form/Form";
import { useQuery } from "@tanstack/react-query";
import { useWatchParameterOnRoutes } from "../../Hooks/useGetParameterOnRoutes";
import { useRedirection } from "../../Hooks/useRedirection";
import { useRelativePath } from "../../Hooks/useNextRelativePath";
import { useRequerPermissoes } from "../../Hooks/useRequerPermissoes";

import { Conjunto } from "../../Components/Conjunto";
import { Link } from "react-router-dom";
import { Ordenar } from "../../Utils/Ordenacao";
import { Permissoes } from "../../Constants/Permissoes";
import { PropriedadesDeNotificacao } from "../../Utils/Notificacoes";
import { PropriedadesDoPopConfirm } from "../../Defaults";
import { ResponsiveAntdTable } from "../../Components/ResponsiveAntdTable";
import { Restrito } from "../../Components/Restrito";
import { TypeVacina } from "../../Models/Domain/Saude/Vacina";
import { VacinaService } from "../../Services/Domain/Saude/VacinaService";

import AdicionarAjuda from "../../Components/AdicionarAjuda";

export const ListagemDeVacinas: React.FC<{
  aoEditar?: (viagem: TypeVacina) => any;
  aoAdicionar?: () => any;

  hrefDoBotaoAdicionar?: string;
  hrefDoBotaoEditar?: (viagem: TypeVacina) => string | void;
}> = (props) => {
  const breakpoints = Grid.useBreakpoint();
  const requerPermissoes = useRequerPermissoes();

  type IFiltro = Parameters<typeof VacinaService.listar>[0];
  const [filtro, setFiltro] = useState<IFiltro>({
    ativo: true,
  });

  const vacinasDisponiveis = useQuery({
    queryKey: [`VacinaService.listar`, filtro],
    queryFn: () => VacinaService.listar(filtro),
  });

  const onFinish = (valores: IFiltro) => {
    setFiltro({
      ...valores,
    });
  };

  const [form] = useForm<IFiltro>();

  const adicionar = requerPermissoes.todas(Permissoes.CriarVacina);
  const ref = useRef(null)

  return (
      <React.Fragment>
          <Form<IFiltro> form={form} onFinish={onFinish} layout="vertical" autoComplete="off">
              <Row gutter={16}>
                  <Col xs={24} sm={16} xl={15}>
                      <Form.Item<IFiltro> name={["nome"]} label="Nome">
                          <Input placeholder="Todos" />
                      </Form.Item>
                  </Col>

                  <Col xs={24} sm={8} xl={4}>
                      <Form.Item<IFiltro> name="ativo" label="Ativo" initialValue={true}>
                          <Select
                              allowClear
                              placeholder={"Todos"}
                              options={[
                                  { value: true, label: "Ativo" },
                                  { value: false, label: "Inativo" },
                              ]}
                          />
                      </Form.Item>
                  </Col>

                  <Col xs={adicionar ? 12 : 24} xl={adicionar ? 2 : 5} style={{ alignItems: "end", display: "flex" }}>
                      <Form.Item style={{ width: "100%" }}>
                          <Button type="primary" htmlType="submit" disabled={vacinasDisponiveis.isFetching} style={{ width: "100%" }}>
                              Listar
                          </Button>
                      </Form.Item>
                  </Col>

                  <Restrito aberto={adicionar}>
                      <Col xs={12} xl={3} style={{ alignItems: "end", display: "flex" }}>
                          <Form.Item style={{ width: "100%" }}>
                              <Link
                                  to={props.hrefDoBotaoAdicionar ?? "#"}
                                  onClick={(e) => {
                                      !props.hrefDoBotaoAdicionar && e.preventDefault();
                                  }}
                              >
                                  <Button
                                      type="primary"
                                      onClick={() => {
                                          props.aoAdicionar?.();
                                      }}
                                      style={{ width: "100%" }}
                                  >
                                      Adicionar
                                  </Button>
                              </Link>
                          </Form.Item>
                      </Col>
                  </Restrito>
              </Row>
          </Form>

          <Row>
              <Col xs={24}>
                  <ResponsiveAntdTable<TypeVacina>
                      bordered
                      rowKey={(k) => k.codigo!}
                      locale={{ emptyText: "Nenhuma viagem encontrada." }}
                      loading={vacinasDisponiveis.isFetching}
                      dataSource={vacinasDisponiveis.data?.data}
                      addTitleOn="lg"
                      columns={[
                          {
                              title: "Código",
                              breakpoints: { xs: 24, sm: 12, xl: 2 },
                              dataIndex: "codigo",
                              key: "vacina-codigo",
                              sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                          },
                          {
                              title: "Ativo",
                              breakpoints: { xs: 24, sm: 12, xl: 2 },
                              dataIndex: "ativo",
                              key: "vacina-ativo",
                              sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                              render: (v) => (v == true ? "Sim" : "Não"),
                          },
                          {
                              title: "Nome",
                              breakpoints: { xs: 24, sm: 24, xl: 16 },
                              dataIndex: "nome",
                              key: "vacina-nome",
                              sorter: (a, b) => Ordenar.String(a.nome, b.nome),
                          },
                          {
                              title: "Ações",
                              key: "acoes",
                              noResponsiveTitle: true,
                              breakpoints: { xs: 24, sm: 24, lg: 4 },
                              render: (_, item) => {
                                  return (
                                      <Conjunto
                                          style={{ justifyContent: breakpoints.sm ? "unset" : "center", display: "flex", flexWrap: "wrap" }}
                                          itens={[
                                              ...(() => {
                                                  if (item.ativo) {
                                                      return [
                                                          requerPermissoes.todas(Permissoes.AtualizarVacina) && (
                                                              <Link
                                                                  to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                  onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                              >
                                                                  <Button
                                                                      type="primary"
                                                                      size="small"
                                                                      onClick={() => {
                                                                          props.aoEditar?.(item);
                                                                      }}
                                                                  >
                                                                      Editar
                                                                  </Button>
                                                              </Link>
                                                          ),
                                                          requerPermissoes.todas(Permissoes.DesativarVacina) && (
                                                              <Popconfirm
                                                                  {...PropriedadesDoPopConfirm("desativar")}
                                                                  onConfirm={() => {
                                                                      if (item.codigo == undefined) return;
                                                                      VacinaService.desativar(item.codigo)
                                                                          .then(() => {
                                                                              vacinasDisponiveis.refetch();
                                                                          })
                                                                          .catch(() => {});
                                                                  }}
                                                              >
                                                                  <Button type="default" danger size="small">
                                                                      Desativar
                                                                  </Button>
                                                              </Popconfirm>
                                                          ),
                                                      ];
                                                  } else {
                                                      return [
                                                          requerPermissoes.todas(Permissoes.AtivarVacina) && (
                                                              <Popconfirm
                                                                  {...PropriedadesDoPopConfirm("ativar")}
                                                                  onConfirm={() => {
                                                                      if (item.codigo == undefined) return;

                                                                      VacinaService.ativar(item.codigo)
                                                                          .then(() => {
                                                                              vacinasDisponiveis.refetch();
                                                                          })
                                                                          .catch(() => {});
                                                                  }}
                                                              >
                                                                  <Button type="dashed" size="small">
                                                                      Reativar
                                                                  </Button>
                                                              </Popconfirm>
                                                          ),
                                                      ];
                                                  }
                                              })(),
                                          ]}
                                      />
                                  );
                              },
                          },
                      ]}
                      csv={{
                          enabled: true,
                          filename: "vacinas.csv",
                          options: {

                          },
                          concat: (a) => {
                            return [
                                {
                                    header: "Data de início",
                                    data: a.map((v) => v.nome ?? ""),
                                },
                            ];
                          }
                      }}
                  />
              </Col>
          </Row>
      </React.Fragment>
  );
};

const CadastroDeVacina: React.FC<{
  aoCancelar?: () => any;
  depoisDeFinalizar?: () => any;
  vacinaEmAlteracao?: TypeVacina;
}> = (props) => {
  const { vacinaEmAlteracao } = props;
  const requerPermissoes = useRequerPermissoes();

  useEffect(() => {
    if (vacinaEmAlteracao != undefined) {
      form.setFieldsValue(vacinaEmAlteracao);
    }
  }, [vacinaEmAlteracao]);

  const [form] = useForm<TypeVacina>();

  const inserir = useMutation({
    mutationFn: (payload: TypeVacina) => VacinaService.inserir(payload),
    onSuccess() {
      notification.open({
        ...PropriedadesDeNotificacao.Sucesso("Cadastro realizado com sucesso."),
      });

      props.depoisDeFinalizar?.();
    },
  });

  const atualizar = useMutation({
    mutationFn: (payload: TypeVacina) => VacinaService.atualizar(payload),
    onSuccess() {
      notification.open({
        ...PropriedadesDeNotificacao.Sucesso("Cadastro atualizado com sucesso."),
      });

      props.depoisDeFinalizar?.();
    },
  });

  const onFinish = (valores: TypeVacina) => {
    const estaEditando = vacinaEmAlteracao != undefined;
    if (estaEditando) {
      atualizar.mutate({ ...vacinaEmAlteracao, ...valores });
    } else {
      inserir.mutate(valores);
    }
  };

  return (
    <React.Fragment>
      <Form<TypeVacina> form={form} onFinish={onFinish} layout="vertical" autoComplete="off" validateTrigger={"onBlur"}>
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item<TypeVacina>
              name={"nome"}
              label={"Nome"}
              rules={[
                {
                  required: true,
                  message: "É necessário específicar o nome!",
                },
                {
                  max: 120,
                  message: "O nome não pode exceder 120 caracteres!",
                },
              ]}
            >
              <Input maxLength={120} placeholder="Nome" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex align="center" justify="center" gap={16}>
              {props.aoCancelar && (
                <Button
                  danger
                  type="primary"
                  key={"cancel"}
                  onClick={() => {
                    props.aoCancelar && props.aoCancelar();
                  }}
                >
                  Cancelar
                </Button>
              )}
              <Button
                type="primary"
                key={"submit"}
                htmlType="submit"
                disabled={
                  inserir.isPending || atualizar.isPending || 
                  props.vacinaEmAlteracao == undefined
                    ? !requerPermissoes.todas(Permissoes.CriarVacina)
                    : !requerPermissoes.todas(Permissoes.AtualizarVacina)
                }
              >
                Salvar
              </Button>
            </Flex>
          </Col>
        </Row>
      </Form>

      <AdicionarAjuda tutoriais={[]} />
    </React.Fragment>
  );
};

export const Vacina: React.FC<{}> = (props) => {
  const [vacinaEmAlteracao, setVacinaEmAlteracao] = useState<TypeVacina | undefined>(undefined);
  const requerPermissoes = useRequerPermissoes();

  const navigate = useNavigate();
  const [raiz, subrota] = useRelativePath();

  const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
  const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

  const obterVacina = useMutation({
    mutationFn: (codigo: number) => VacinaService.listar({ codigo, ativo: true }),
    onSuccess: (resposta) => {
      const resultados = resposta.data;
      if (resultados.length == 0) {
        notification.open({
          ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado"),
        });
        navigate(raiz);
      } else {
        const vacina = resposta.data.at(0);
        setVacinaEmAlteracao(vacina);
      }
    },
    onError: () => {
      navigate(raiz);
    },
  });

  useRedirection([Permissoes.ListarVacinas, Permissoes.AtualizarVacina, Permissoes.CriarVacina, Permissoes.AtivarVacina, Permissoes.DesativarVacina]);
  useWatchParameterOnRoutes({
    appliablePaths: [rotaDeEdicao, rotaDeVisualizacao],
    paramToWatch: "id",
    filters: [(p) => isNaN(Number(p))],
    onNotAccepted() {
      navigate(raiz);
    },
    onExists(param) {
      obterVacina.mutate(Number(param));
    },
  });

  return (
    <>
      <h2 style={{ marginTop: 0 }}>{rotaDeVisualizacao ? "Detalhes da vacina" : rotaDeEdicao ? "Edição da vacina" : "Cadastro de vacina"}</h2>

      <Divider />

      <Routes>
        {
          <Route
            path="*"
            index
            element={
              <ListagemDeVacinas
                hrefDoBotaoAdicionar={raiz + "/cadastro"}
                hrefDoBotaoEditar={(item) => {
                  if (item.codigo) {
                    return raiz + `/editar?id=${item.codigo}`;
                  }
                }}
                aoEditar={(item) => {
                  setVacinaEmAlteracao(item);
                }}
              />
            }
          />
        }

        {requerPermissoes.todas(Permissoes.CriarVacina) && (
          <Route
            path="/cadastro"
            element={
              <CadastroDeVacina
                aoCancelar={() => {
                  navigate(raiz);
                }}
                depoisDeFinalizar={() => {
                  navigate(raiz);
                }}
              />
            }
          />
        )}

        {requerPermissoes.todas(Permissoes.AtualizarVacina) && (
          <Route
            path="/editar"
            element={
              vacinaEmAlteracao == undefined ? (
                <Skeleton active />
              ) : (
                <CadastroDeVacina
                  vacinaEmAlteracao={vacinaEmAlteracao}
                  aoCancelar={() => {
                    navigate(raiz);
                  }}
                  depoisDeFinalizar={() => {
                    navigate(raiz);
                  }}
                />
              )
            }
          />
        )}
      </Routes>
    </>
  );
};

export default Vacina;
